import React, { useEffect, useState } from "react"
import { Card, Popover } from "react-bootstrap"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { getPortStaysData } from "../helpers/getPortStaysData"
import { getProlongedStay } from "../helpers/getProlongedStay"
import { AlertTriangle } from "lucide-react"
import { triangleStyles } from "../helpers/getProlongedStay"
import { formatDateToShortMonthYear } from "../helpers/getProlongedStay"
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Tooltip from "react-bootstrap/Tooltip"

interface PrevetProlongedStayWidgetProps {
  vesselStaysData: VesselStay[] | undefined
  isVesselStaysLoading: boolean
  isVesselStaysError: boolean
}

function PrevetProlongedStayWidget({
  vesselStaysData
}: PrevetProlongedStayWidgetProps) {
  const portStaysData =
    vesselStaysData && getPortStaysData(vesselStaysData).days

  const { result, consecutiveDates, prolongedPort } = portStaysData
    ? getProlongedStay(portStaysData)
    : {
        result: "low",
        consecutiveDates: [],
        prolongedPort: null,
      }

  const currentStyle =
    result === "high" ? triangleStyles.high : triangleStyles.medium

  const renderTooltip = () => {
    return (
      <Popover id="popover-basic">
        <Popover.Body>Ask for UWC report</Popover.Body>
      </Popover>
    )
  }

  const renderTooltipProlongedStay = () => {
    return (
      <Popover id="popover-basic">
        <Popover.Body>Data from last 6 months</Popover.Body>
      </Popover>
    )
  }

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)

    //cleanup
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [window.innerWidth])

  function truncatePortName(portName: string, maxLength = 5) {
    if (portName) {
      // Truncate only if screen width is below 760px (mobile)
      if (screenWidth < 1660 && screenWidth >= 760) {
        maxLength = 8
        return portName.length > maxLength
          ? `${portName.slice(0, maxLength)}..`
          : portName
      } else if (screenWidth >= 2140 && screenWidth <= 3500) {
        maxLength = 7
        return portName.length > maxLength
          ? `${portName.slice(0, maxLength)}..`
          : portName
      } else if (screenWidth < 2140 && screenWidth >= 1660) {
        maxLength = 6
        return portName.length > maxLength
          ? `${portName.slice(0, maxLength)}..`
          : portName
      }
      // Default behavior for other screen sizes
      return portName
    }
    return
  }

  return (
      <Card className="PrevetContainer_ProlongedStayWidget w-100">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex">
              <Card.Title>Prolonged Stay</Card.Title>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltipProlongedStay()}
              >
                <i
                  className="ti-info-alt ms-1"
                  style={{ position: "relative", fontSize: "0.65rem" }}
                />
              </OverlayTrigger>
            </div>

            {result != "low" && (
              <OverlayTrigger placement="top" overlay={renderTooltip()}>
                <div className="d-flex flex-column align-items-center gap-0">
                  <AlertTriangle
                    style={{
                      fill: currentStyle.fill,
                      stroke: currentStyle.stroke,
                      strokeWidth: 2,
                    }}
                    size={32}
                  />
                  <div className="text-muted fs-6">Foul Risk</div>
                </div>
              </OverlayTrigger>
            )}
          </div>
          {/* Foul risk */}
          <div className="w-full mt-4">
            {prolongedPort ? (
              <div className="d-flex justify-content-between align-items-baseline mx-auto">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${prolongedPort.port}`}>
                      {prolongedPort.port}
                    </Tooltip>
                  }
                >
                  <div
                    className="text-muted"
                    style={{ width: "33%", cursor: "pointer" }}
                  >
                    {truncatePortName(prolongedPort.port, 5)}
                  </div>
                </OverlayTrigger>
                <div
                  className="d-flex gap-0 align-items-end"
                  style={{ width: "33%" }}
                >
                  <div className="d-inline-flex align-items-baseline">
                    <h1
                      className={
                        result == "high" ? "text-danger" : "text-warning"
                      }
                    >
                      {prolongedPort.duration}
                    </h1>
                    <h4
                      className={
                        result == "high" ? "text-danger" : "text-warning"
                      }
                    >
                      D
                    </h4>
                  </div>
                </div>
                <div className="text-muted h-100">
                  {formatDateToShortMonthYear(prolongedPort.from)}
                </div>
              </div>
            ) : (
              <div className="d-flex w-100 text-center justify-content-center align-items-center">
                No prolonged stay {"> "}20D found
              </div>
            )}
          </div>
          {/* Divider */}
          <div className="w-full my-1 h-1 border border-top border-light" />
          {/* Consecutives */}
          <div className="w-full mt-2">
            {consecutiveDates.length > 0 && consecutiveDates.length >= 2 ? (
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted" style={{ width: "33%" }}>
                  {">"} 10D recurring
                </div>
                <h1
                  className={
                    consecutiveDates.length > 2
                      ? "text-danger text-center"
                      : "text-warning text-center"
                  }
                >
                  {consecutiveDates.length}
                </h1>
                <div
                  className="text-muted text-end"
                  style={{
                    width: "33%",
                    maxHeight: consecutiveDates.length > 3 ? "60px" : "auto",
                    overflowY:
                      consecutiveDates.length > 3 ? "scroll" : "visible",
                  }}
                >
                  {Array.from({ length: consecutiveDates.length }).map(
                    (_, i) => (
                      <div key={i}>
                        {formatDateToShortMonthYear(consecutiveDates[i].from)}
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="d-flex w-100 text-center justify-content-center align-items-center">
                No {">"} 10d recurring stays found
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    
  )
}

export default PrevetProlongedStayWidget
